import {
    RestApiDayAvailabilityDto,
    RestApiDeliveryMethodTimeRestrictionDto,
    RestApiTimePlanIntervalDto,
} from '@order-mage/integration-sdk';
import dayjs from 'dayjs';
import { useCallback } from 'react';

export const isAvailableNow = (timeAvailability: RestApiDayAvailabilityDto[]): boolean => {
    if (!timeAvailability.length) return true;

    const now = dayjs();
    return timeAvailability.some((day) =>
        day.availableIntervals?.some((interval) => {
            const start = dayjs(interval.start);
            const end = dayjs(interval.end);
            return (now.isAfter(start) || now.isSame(start)) && (now.isBefore(end) || now.isSame(end));
        }),
    );
};

const WEEKDAY_CYCLE_MAP: Record<string, number> = {
    EVERY_SU: 0,
    EVERY_MO: 1,
    EVERY_TU: 2,
    EVERY_WE: 3,
    EVERY_TH: 4,
    EVERY_FR: 5,
    EVERY_SA: 6,
};

const isIntervalActiveNow = (interval: RestApiTimePlanIntervalDto, now: dayjs.Dayjs): boolean => {
    const [hours, minutes] = interval.time.split(':').map(Number);

    // Check if now is within any active range
    if (interval.ranges.length > 0) {
        const isInRange = interval.ranges.some((range) => {
            const start = dayjs(range.startTimestamp);
            const end = range.endTimestamp ? dayjs(range.endTimestamp) : null;
            return (
                (now.isAfter(start) || now.isSame(start)) && (!end || now.isBefore(end) || now.isSame(end))
            );
        });
        if (!isInRange) return false;
    }

    const { repeatCycle } = interval;

    // Check if today matches the repeat cycle
    if (repeatCycle.length > 0 && !repeatCycle.includes('EVERY_DAY')) {
        const todayWeekday = now.day();
        const matchesWeekday = repeatCycle.some((cycle) => WEEKDAY_CYCLE_MAP[cycle] === todayWeekday);
        if (!matchesWeekday) return false;
    }

    // Compare on the wall clock: adding `duration` to an instant drifts by an hour on a
    // daylight-saving day, so "00:00 for 1275 min" would end at 20:15 instead of 21:15.
    const minuteOfDay = now.hour() * 60 + now.minute() + now.second() / 60;
    const startMinute = hours * 60 + minutes;
    return minuteOfDay >= startMinute && minuteOfDay <= startMinute + interval.duration;
};

/**
 * Checks if an area is available at `at` (default: now) based on its restrictAvailableTime time plans.
 * Empty restrictions = always available.
 * Within a plan: UNAVAILABLE overrides AVAILABLE.
 * Across plans: union of AVAILABLE (any plan making it available is enough).
 */
export const isTimePlanActiveNow = (
    restrictions: RestApiDeliveryMethodTimeRestrictionDto[],
    at: dayjs.Dayjs = dayjs(),
): boolean => {
    if (!restrictions.length) return true;

    const now = at;

    for (const restriction of restrictions) {
        const { intervals } = restriction.timePlan;
        let hasAvailable = false;
        let hasUnavailable = false;

        for (const interval of intervals) {
            if (isIntervalActiveNow(interval, now)) {
                if (interval.type === 'AVAILABLE') {
                    hasAvailable = true;
                } else if (interval.type === 'UNAVAILABLE') {
                    hasUnavailable = true;
                }
            }
        }

        // Within a plan: UNAVAILABLE overrides AVAILABLE
        // Across plans: union — any plan making it available is enough
        if (hasAvailable && !hasUnavailable) return true;
    }

    return false;
};

export const useIsTimeAvailable = () => {
    return useCallback((timeAvailability: RestApiDayAvailabilityDto[]) => {
        return isAvailableNow(timeAvailability);
    }, []);
};
